import { ACTIVITY_TYPE_LABELS } from '../../constants/activityTypes.js'
/**
 * Kit do módulo Atendimento — helpers puros de UI (cores, formatação, avatar).
 * Sem estado; reaproveitado pela lista, timeline e painel do contato.
 */

export const FILTERS = [
  { id: 'todas', label: 'Todas' },
  { id: 'nao_lidas', label: 'Não lidas' },
  { id: 'minhas', label: 'Minhas' },
  { id: 'nao_atribuidas', label: 'Não atribuídas' },
  { id: 'arquivadas', label: 'Arquivadas' },
]

export const STATUS_LABEL = {
  aberta: 'Aberta',
  encerrada: 'Encerrada',
  arquivada: 'Arquivada',
}

// rótulos dos tipos de atividade do CRM (painel lateral, Fase 4)
export const ACT_TIPO = ACTIVITY_TYPE_LABELS

// metadados de canal p/ badge no inbox/header (interno não mostra selo)
export const CHANNEL_META = {
  whatsapp:  { icon: 'whatsapp', label: 'WhatsApp' },
  instagram: { icon: 'attend',   label: 'Instagram' },
  messenger: { icon: 'attend',   label: 'Messenger' },
  email:     { icon: 'mail',     label: 'E-mail' },
}

// DDDs brasileiros válidos — distingue "(11) 98644-5241" de um internacional
// gravado sem "+" com o mesmo tamanho (EUA "1260…", Chile "569…").
const BR_DDD = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28,
  31, 32, 33, 34, 35, 37, 38, 41, 42, 43, 44, 45, 46, 47, 48, 49,
  51, 53, 54, 55, 61, 62, 63, 64, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98, 99,
])

// mesma regra de api/utils/phone.py: 11 dígitos com 9 após o DDD, ou 10 com 2-9
function isNationalBr(d) {
  if (![10, 11].includes(d.length) || !BR_DDD.has(Number(d.slice(0, 2)))) return false
  return d.length === 11 ? d[2] === '9' : d[2] >= '2'
}

// telefone (qualquer máscara) → jid do WhatsApp p/ iniciar thread pela UI.
// Lead salvo sem código do país ganha o 55 — sem ele a Evolution não acha o número.
export function phoneToJid(phone) {
  const text = String(phone || '').trim()
  const d = text.replace(/\D/g, '')
  if (!d) return null
  const number = !text.startsWith('+') && isNationalBr(d) ? `55${d}` : d
  return `${number}@s.whatsapp.net`
}

const AVATAR_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f59e0b',
  '#10b981', '#06b6d4', '#3b82f6', '#84cc16', '#ef4444',
]

export function avatarColor(seed = '') {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xffff
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

export function initials(nome = '') {
  const parts = String(nome).trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function displayName(conv) {
  return conv?.contact_nome || conv?.company_nome || 'Contato sem nome'
}

// timestamp relativo curto p/ a lista ("agora", "12:40", "ontem", "9 jul")
export function relTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const diff = (now - d) / 1000
  if (diff < 60) return 'agora'
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  const y = new Date(now); y.setDate(now.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return 'ontem'
  return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
}

// hora cheia p/ o balão da mensagem
export function msgTime(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

// rótulo do separador de data na timeline
function dateSep(iso) {
  const d = new Date(iso)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return 'Hoje'
  const y = new Date(now); y.setDate(now.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return 'Ontem'
  return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
}

// valor monetário BRL (nulo se vazio/NaN) — usado nos negócios do painel
export function money(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  if (Number.isNaN(n)) return null
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

// prazo curto de atividade ("15 jul 14:00"); só a data se sem hora significativa
export function prazoLabel(iso) {
  if (!iso) return null
  const d = new Date(iso)
  const data = d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return hora === '00:00' ? data : `${data} ${hora}`
}

/* ------------------------------------------------------------------------ */
/* Composer (v2) — emojis e respostas rápidas                                */
/* ------------------------------------------------------------------------ */
export const EMOJIS = [
  '😀', '😁', '😂', '🙂', '😉', '😊', '😍', '🤝',
  '👍', '👏', '🙏', '🔥', '✅', '⚠️', '💡', '🚀',
]

export const QUICK_REPLIES = [
  { id: 'saudacao', label: 'Saudação', texto: 'Olá! Tudo bem? Como posso ajudar?' },
  { id: 'aguarde', label: 'Pedir um instante', texto: 'Só um instante, já verifico isso para você.' },
  { id: 'proposta', label: 'Enviar proposta', texto: 'Vou preparar a proposta e te envio ainda hoje.' },
  { id: 'reuniao', label: 'Sugerir reunião', texto: 'Podemos marcar uma call rápida para alinhar os detalhes?' },
  { id: 'encerrar', label: 'Encerrar atendimento', texto: 'Fico à disposição. Qualquer dúvida é só chamar!' },
]

/* ------------------------------------------------------------------------ */
/* Painel de contexto (v2) — jornada, oportunidade, atividades               */
/* ------------------------------------------------------------------------ */
export const JOURNEY_STEPS = [
  { id: 'lead', label: 'Lead' },
  { id: 'oportunidade', label: 'Oportunidade' },
  { id: 'cliente', label: 'Cliente' },
]

// etapa da jornada a partir dos negócios do contato: sem negócio = lead,
// negócio aberto = oportunidade, negócio ganho = cliente.
export function journeyStage(deals = []) {
  if (!Array.isArray(deals) || !deals.length) return 'lead'
  const ganho = deals.some((d) => d.status === 'ganho' || d.is_won === true)
  return ganho ? 'cliente' : 'oportunidade'
}

// código curto da oportunidade ("OP-2024-0841"); cai no id quando o CRM não
// tem código próprio.
export function opCode(deal) {
  if (!deal) return ''
  if (deal.codigo) return deal.codigo
  const ano = new Date(deal.created_at || Date.now()).getFullYear()
  const seq = String(deal.numero ?? deal.id ?? '').replace(/\D/g, '').slice(-4).padStart(4, '0')
  return `OP-${ano}-${seq}`
}

// ícone da linha de atividade recente (usa o tipo do CRM)
export function actIcon(act) {
  switch (act?.tipo) {
    case 'mensagem': return 'whatsapp'
    case 'ligacao': return 'phone'
    case 'reuniao': return 'calendar'
    case 'evento': return 'calendar'
    case 'pagamento': return 'currency'
    case 'follow_up': return 'refresh'
    default: return 'attend'
  }
}

// agrupa mensagens por dia p/ inserir separadores; devolve [{sep, items:[...]}]
export function groupByDay(messages) {
  const groups = []
  let cur = null
  for (const m of messages) {
    const key = new Date(m.created_at).toDateString()
    if (!cur || cur.key !== key) {
      cur = { key, sep: dateSep(m.created_at), items: [] }
      groups.push(cur)
    }
    cur.items.push(m)
  }
  return groups
}
